import {Injectable}                          from '@angular/core';
import {Validators}                          from '@angular/forms';
import {CampaignYearStore}                   from '../../../../stores/login/campaignyear.store';
import {nameOf}                              from '../../../../ap-core/utils/ap-name-of';
import {ApDynformsConfigFieldset}            from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigComboBox}            from '../../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {DriverStore}                         from '../../../../stores/docu/driver.store';
import {MachineStore}                        from '../../../../stores/docu/machine.store';
import {InstrumentStore}                     from '../../../../stores/docu/instrument.store';
import {ApDynformsConfigDatepicker}          from '../../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {ApDynformsValidator}                 from '../../../../ap-dynforms/ap-dynforms-validator';
import {ApDynformsConfigPlaceholder}         from '../../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {ApDynformsConfigTimepicker}          from '../../../../ap-dynforms/config/ap-dynforms-config-timepicker';
import {ApDynformsConfigBase}                from '../../../../ap-dynforms/config/ap-dynforms-config-base';
import {FwEntryComponentsService}            from '../../services/fw-entry-components.service';
import {IFwPlanPlanningWizardSingleEditData} from '../fw-plan-planning.types';
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;

@Injectable()
export class FwPlanPlanningCommonConfig {
  constructor(private driverStore: DriverStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private campaignYearStore: CampaignYearStore,
              private fwEntryComponentService: FwEntryComponentsService) {
  }

  /**
   * Gets a fieldset configuration for general information including application date, start time and hectares per hour
   * @param data The wizard data containing current date, time, and hectares per hour selections
   * @param useStartTimeField Whether to use time fields for Start
   * @param useMaxWidth Whether to use maximum width for the fieldset
   * @returns ApDynformsConfigFieldset containing date picker and time picker controls for general information
   * @public
   */
  public getGeneralFieldSet(data: IFwPlanPlanningWizardSingleEditData, useStartTimeField: boolean, useMaxWidth = false): ApDynformsConfigFieldset {
    const campaignYear = this.campaignYearStore.SelectedCampaignYear.Year;
    const minAndMaxRange = this.fwEntryComponentService.getMinAndMaxDateRangesForPlanBook(campaignYear);
    const fieldSet = new ApDynformsConfigFieldset({
      key: 'General',
      legend: 'Global__General_Information',
      useMaxWidth,
      config: [
        new ApDynformsConfigDatepicker({
          key: nameOf<IFwPlanPlanningWizardSingleEditData>('ApplicationDate'),
          label: 'Nutrients__FertilizationDate',
          minDate: minAndMaxRange.Min,
          maxDate: minAndMaxRange.Max,
          value: data.ApplicationDate,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        })
      ]
    });
    if (useStartTimeField) {
      fieldSet.config.push(
        new ApDynformsConfigTimepicker({
          key: nameOf<IFwPlanPlanningWizardSingleEditData>('Start'),
          label: 'Docu_Ini__StartTime',
          format: 'HH:mm',
          secSteps: 60,
          value: data.Start
        })
      );
    } else {
      fieldSet.config.push(new ApDynformsConfigPlaceholder());
    }
    return fieldSet;
  }

  /**
   * Retrieves the configuration objects for dynamic forms related to resources.
   * @param data - The input data containing initial values for the dynamic forms,
   * such as machine, instrument, and driver information.
   * @return An array of dynamic form configuration objects, each representing a specific resource configuration
   * (e.g., machine, instrument, driver).
   */
  public getResourcesConfigs(data: IFwPlanPlanningWizardSingleEditData): ApDynformsConfigBase<any>[] {
    return [
      new ApDynformsConfigComboBox({
        key: nameOf<IFwPlanPlanningWizardSingleEditData>('Machine'),
        label: 'Settings__Lbl_Menu_Devices',
        textField: nameOf<IMachine>('NameWithSign'),
        valueField: nameOf<IMachine>('Id'),
        options: this.machineStore.Machines$,
        value: data.Machine,
        sort: 1,
        valuePrimitive: true
      }),
      new ApDynformsConfigComboBox({
        key: nameOf<IFwPlanPlanningWizardSingleEditData>('Instrument'),
        label: 'Docu_Ini__Tool',
        textField: nameOf<IInstrument>('Name'),
        valueField: nameOf<IInstrument>('Id'),
        options: this.instrumentStore.Instruments$,
        value: data.Instrument,
        sort: 1,
        valuePrimitive: true
      }),
      new ApDynformsConfigComboBox({
        key: nameOf<IFwPlanPlanningWizardSingleEditData>('Driver'),
        label: 'Docu_Ini__Motorist',
        textField: nameOf<IDriver>('FirstNameFullName'),
        valueField: nameOf<IDriver>('Id'),
        options: this.driverStore.Drivers$,
        value: data.Driver,
        sort: 1,
        valuePrimitive: true
      }),
    ];
  }
}
